import { DashboardEvent } from '../types/events.js';
import { BUCKET_COUNT, BUCKET_VIRTUAL_MS } from '../types/constants.js';
import { BAR_GAP, MAX_COMPANIES, ROLL_MS } from './config.js';
import type { DomRefs } from './dom.js';
import { fmtHm, fnvHue } from './utils.js';

const OTHER_COMPANY_KEY = '__other__';

interface Bucket {
  startAt: number | null;
  readonly byCompany: Map<string, number>;
  failure: number;
}

interface Bar {
  readonly el: HTMLElement;
  readonly stack: HTMLElement;
  readonly failure: HTMLElement;
  readonly labelEl: HTMLElement;
  readonly segs: Map<string, HTMLElement>;
}

function makeBucket(startAt: number | null): Bucket {
  return { startAt, byCompany: new Map(), failure: 0 };
}

function bucketStartFor(atMs: number): number {
  return Math.floor(atMs / BUCKET_VIRTUAL_MS) * BUCKET_VIRTUAL_MS;
}

export interface ChartCallbacks {
  /** direction=+1: 1 バケット前進 / -1: ロールの取り消し (1 バケット巻き戻し) */
  readonly onRoll: (direction: 1 | -1) => void;
}

export class VolumeChart {
  private readonly buckets: Bucket[] = [];
  private readonly bars: Bar[] = [];
  private anchorStart: number | null = null;
  private isRolling = false;
  private rollEndHandle: number | null = null;
  private barWidth = 20;
  private readonly companyOrder: string[] = [];
  private readonly companyColor: Map<string, string> = new Map();
  private inBatch = false;
  private batchDirty = false;

  constructor(
    private readonly dom: DomRefs,
    private readonly callbacks: ChartCallbacks,
  ) {
    for (let i = 0; i < BUCKET_COUNT; i++) {
      this.buckets.push(makeBucket(null));
      const bar = this.makeBar();
      this.dom.chartBars.appendChild(bar.el);
      this.bars.push(bar);
    }
    this.measureBarWidth();
    window.addEventListener('resize', () => this.measureBarWidth());
  }

  get bucketCount(): number {
    return BUCKET_COUNT;
  }

  get bucketVirtualMs(): number {
    return BUCKET_VIRTUAL_MS;
  }

  /** 12h 窓内に少なくとも 1 件の RawDataUpload が積まれている社数。OTHER は除外。 */
  getActiveCompanyCount(): number {
    const seen = new Set<string>();
    for (const bucket of this.buckets) {
      for (const key of bucket.byCompany.keys()) {
        if (key !== OTHER_COMPANY_KEY) seen.add(key);
      }
    }
    return seen.size;
  }

  reset(): void {
    this.anchorStart = null;
    this.buckets.length = 0;
    for (let i = 0; i < BUCKET_COUNT; i++) this.buckets.push(makeBucket(null));
    this.rebuildAllBars();
  }

  /** silent な大量 dispatch (シーク) 中の DOM 操作を抑制するモード。 */
  beginBatch(): void {
    this.cancelActiveRollIfAny();
    this.inBatch = true;
    this.batchDirty = false;
  }

  endBatch(): void {
    const wasDirty = this.batchDirty;
    this.inBatch = false;
    this.batchDirty = false;
    if (wasDirty) {
      this.rebuildAllBars();
    }
  }

  addEvent(event: DashboardEvent): void {
    // 全社 Volume チャートは「アップロード件数」だけを積む (spec2)
    if (event.eventName !== 'RawDataUpload') return;
    const atMs = new Date(event.timestamp).getTime();
    if (Number.isNaN(atMs)) return;
    if (this.anchorStart === null) this.initAnchor(atMs);

    const target = bucketStartFor(atMs);

    // event の bucket が現 last より先なら last を進める
    const lastVisible = this.buckets[this.lastVisibleIndex()];
    if (lastVisible && lastVisible.startAt !== null && target > lastVisible.startAt) {
      if (this.inBatch) {
        if (this.anchorStart !== null) {
          const needed = Math.max(
            0,
            Math.round((target - this.anchorStart) / BUCKET_VIRTUAL_MS),
          );
          for (let i = 0; i < needed; i++) this.shiftOneBucketImmediate();
          this.batchDirty = true;
        }
      } else {
        this.rollForVirtualTime(atMs);
      }
    }

    // 適切な bucket index を見つけて格納 (last だけでなく窓内のどこでも置ける)
    const idx = this.bucketIndexForTarget(target);
    if (idx < 0) return;
    const bucket = this.buckets[idx];
    if (!bucket || bucket.startAt === null) return;

    const key = this.resolveCompany(event.metadata.companyName);
    const prev = bucket.byCompany.get(key) ?? 0;
    bucket.byCompany.set(key, prev + 1);

    if (this.inBatch) {
      this.batchDirty = true;
      return;
    }

    this.renderChart();
    const popBar = this.bars[idx];
    if (popBar) {
      popBar.el.classList.remove('is-pop');
      void popBar.el.offsetWidth;
      popBar.el.classList.add('is-pop');
    }
  }

  /**
   * cursor (virtual now) に合わせて last バケットを進める。
   * イベントが来ない静かな時間帯でも 10 分おきにバーが流れるための駆動。
   */
  advanceTo(virtualMs: number): void {
    if (this.anchorStart === null) return;
    if (this.inBatch) return;
    if (Number.isNaN(virtualMs)) return;
    const target = bucketStartFor(virtualMs);
    if (target <= this.anchorStart) return;
    this.rollForVirtualTime(virtualMs);
  }

  private lastVisibleIndex(): number {
    return this.isRolling ? this.buckets.length - 2 : this.buckets.length - 1;
  }

  private bucketIndexForTarget(target: number): number {
    if (this.anchorStart === null) return -1;
    const lastIdx = this.lastVisibleIndex();
    const offset = Math.round((target - this.anchorStart) / BUCKET_VIRTUAL_MS);
    const idx = lastIdx + offset;
    if (idx < 0 || idx >= this.buckets.length) return -1;
    return idx;
  }

  private initAnchor(atMs: number): void {
    const anchor = bucketStartFor(atMs);
    this.anchorStart = anchor;
    for (let i = 0; i < BUCKET_COUNT; i++) {
      const startAt = anchor - (BUCKET_COUNT - 1 - i) * BUCKET_VIRTUAL_MS;
      this.buckets[i] = makeBucket(startAt);
    }
    if (!this.inBatch) this.renderChart();
  }

  private resolveCompany(name: string): string {
    if (this.companyColor.has(name)) return name;
    if (this.companyOrder.length >= MAX_COMPANIES) return OTHER_COMPANY_KEY;
    const hue = fnvHue(name);
    this.companyColor.set(name, `hsl(${hue}, 62%, 58%)`);
    this.companyOrder.push(name);
    // batch 中は endBatch で全 bar を rebuild するので個別 ensure は不要
    if (!this.inBatch) {
      for (const bar of this.bars) this.ensureCompanySegments(bar);
    }
    return name;
  }

  private rollForVirtualTime(atMs: number): void {
    if (this.anchorStart === null) return;
    const target = bucketStartFor(atMs);
    const needed = Math.max(0, Math.round((target - this.anchorStart) / BUCKET_VIRTUAL_MS));
    if (needed === 0) return;

    // アニメ中でもなく、ちょうど 1 本進むだけなら左スライドアニメで見せる
    if (!this.isRolling && needed === 1) {
      this.animateSingleRoll();
      return;
    }

    // それ以外 (複数本 or アニメ途中) は data を即時進行し DOM を再構築する
    this.cancelActiveRollIfAny();
    for (let i = 0; i < needed; i++) this.shiftOneBucketImmediate();
    this.rebuildAllBars();
  }

  private shiftOneBucketImmediate(): void {
    if (this.anchorStart === null) return;
    this.anchorStart += BUCKET_VIRTUAL_MS;
    this.buckets.shift();
    this.buckets.push(makeBucket(this.anchorStart));
    this.callbacks.onRoll(1);
  }

  private cancelActiveRollIfAny(): void {
    if (!this.isRolling) return;
    if (this.rollEndHandle !== null) {
      window.clearTimeout(this.rollEndHandle);
      this.rollEndHandle = null;
    }
    // animateSingleRoll で「末尾に push」した進行を巻き戻すので pop 側で取り消す。
    // anchorStart も対応分だけ戻し、roster には direction=-1 で逆方向ロールを通知。
    while (this.buckets.length > BUCKET_COUNT) {
      this.buckets.pop();
      if (this.anchorStart !== null) this.anchorStart -= BUCKET_VIRTUAL_MS;
      this.callbacks.onRoll(-1);
    }
    while (this.bars.length > BUCKET_COUNT) {
      const removed = this.bars.pop();
      if (removed) removed.el.remove();
    }
    this.dom.chartBars.style.transition = 'none';
    this.dom.chartBars.style.transform = 'translateX(0)';
    void this.dom.chartBars.offsetWidth;
    this.isRolling = false;
  }

  private animateSingleRoll(): void {
    if (this.anchorStart === null) return;
    this.isRolling = true;

    this.anchorStart += BUCKET_VIRTUAL_MS;
    this.buckets.push(makeBucket(this.anchorStart));
    const newBar = this.makeBar();
    this.dom.chartBars.appendChild(newBar.el);
    this.bars.push(newBar);

    this.callbacks.onRoll(1);
    this.renderChart();

    requestAnimationFrame(() => {
      this.dom.chartBars.style.transition = `transform ${ROLL_MS}ms linear`;
      this.dom.chartBars.style.transform = `translateX(-${this.barWidth + BAR_GAP}px)`;
    });

    this.rollEndHandle = window.setTimeout(() => {
      this.rollEndHandle = null;
      this.buckets.shift();
      const removed = this.bars.shift();
      if (removed) removed.el.remove();
      this.dom.chartBars.style.transition = 'none';
      this.dom.chartBars.style.transform = 'translateX(0)';
      void this.dom.chartBars.offsetWidth;
      this.isRolling = false;
      this.renderChart();
    }, ROLL_MS + 20);
  }

  private renderChart(): void {
    let peak = 1;
    const sums: number[] = [];
    for (const b of this.buckets) {
      let successSum = 0;
      for (const v of b.byCompany.values()) successSum += v;
      sums.push(successSum);
      const total = successSum + b.failure;
      if (total > peak) peak = total;
    }
    const currentIdx = this.isRolling ? this.bars.length - 2 : this.bars.length - 1;
    for (let i = 0; i < this.bars.length; i++) {
      const bar = this.bars[i];
      const b = this.buckets[i];
      if (!bar || !b) continue;
      this.ensureCompanySegments(bar);
      const successSum = sums[i] ?? 0;
      bar.stack.style.height = `${(successSum / peak) * 100}%`;
      bar.failure.style.height = `${(b.failure / peak) * 100}%`;
      for (const [name, seg] of bar.segs) {
        const count = b.byCompany.get(name) ?? 0;
        const basis = successSum > 0 ? (count / successSum) * 100 : 0;
        seg.style.flexBasis = `${basis}%`;
      }
      bar.el.classList.toggle('is-current', i === currentIdx);
      const canLabel = b.startAt !== null && new Date(b.startAt).getMinutes() === 0;
      bar.el.classList.toggle('has-label', canLabel);
      if (canLabel) {
        bar.labelEl.textContent = fmtHm(b.startAt);
      }
    }
    this.dom.chartPeak.textContent = String(peak);
  }

  private makeBar(): Bar {
    const li = document.createElement('li');
    li.className = 'chart__bar';
    li.innerHTML =
      '<div class="seg-stack"></div><div class="seg seg--failure"></div><div class="chart__bar-label"></div>';
    li.style.flex = `0 0 ${this.barWidth}px`;
    const stack = li.querySelector('.seg-stack');
    const failure = li.querySelector('.seg--failure');
    const labelEl = li.querySelector('.chart__bar-label');
    if (
      !(stack instanceof HTMLElement) ||
      !(failure instanceof HTMLElement) ||
      !(labelEl instanceof HTMLElement)
    ) {
      throw new Error('bar internal elements missing');
    }
    const bar: Bar = {
      el: li,
      stack,
      failure,
      labelEl,
      segs: new Map(),
    };
    this.ensureCompanySegments(bar);
    return bar;
  }

  private ensureCompanySegments(bar: Bar): void {
    if (!bar.segs.has(OTHER_COMPANY_KEY)) {
      const seg = document.createElement('div');
      seg.className = 'seg-company seg-company--other';
      seg.dataset['company'] = OTHER_COMPANY_KEY;
      seg.style.background = 'rgba(140, 150, 180, 0.45)';
      bar.stack.appendChild(seg);
      bar.segs.set(OTHER_COMPANY_KEY, seg);
    }
    for (const name of this.companyOrder) {
      if (bar.segs.has(name)) continue;
      const color = this.companyColor.get(name);
      if (color === undefined) continue;
      const seg = document.createElement('div');
      seg.className = 'seg-company';
      seg.dataset['company'] = name;
      seg.style.background = color;
      seg.title = name;
      bar.stack.appendChild(seg);
      bar.segs.set(name, seg);
    }
  }

  private rebuildAllBars(): void {
    this.dom.chartBars.innerHTML = '';
    this.bars.length = 0;
    for (let i = 0; i < BUCKET_COUNT; i++) {
      const bar = this.makeBar();
      this.dom.chartBars.appendChild(bar.el);
      this.bars.push(bar);
    }
    this.dom.chartBars.style.transition = 'none';
    this.dom.chartBars.style.transform = 'translateX(0)';
    void this.dom.chartBars.offsetWidth;
    // 新規生成の seg は初期値 0% から transition で target% へ伸びるが、
    // silent dispatch などで rebuild が連発すると伸びかけで歯抜けに見える。
    // 一度 transition なしで target にスナップさせる。
    this.withTransitionsDisabled(() => this.renderChart());
  }

  private withTransitionsDisabled(fn: () => void): void {
    const undo: Array<() => void> = [];
    for (const bar of this.bars) {
      const els: HTMLElement[] = [bar.stack, bar.failure, ...bar.segs.values()];
      for (const el of els) {
        const prev = el.style.transition;
        el.style.transition = 'none';
        undo.push(() => {
          el.style.transition = prev;
        });
      }
    }
    fn();
    void this.dom.chartBars.offsetWidth;
    for (const restore of undo) restore();
  }

  private measureBarWidth(): void {
    const cs = getComputedStyle(this.dom.chartViewport);
    const pl = parseFloat(cs.paddingLeft) || 0;
    const pr = parseFloat(cs.paddingRight) || 0;
    const rect = this.dom.chartViewport.getBoundingClientRect();
    const inner = rect.width - pl - pr;
    const w = (inner - BAR_GAP * (BUCKET_COUNT - 1)) / BUCKET_COUNT;
    this.barWidth = Math.max(2, w);
    for (const b of this.bars) {
      b.el.style.flex = `0 0 ${this.barWidth}px`;
    }
  }
}
